import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  DocumentTextIcon,
  SparklesIcon,
  ClipboardDocumentIcon,
  ArrowDownTrayIcon,
  AdjustmentsHorizontalIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline';
import { Helmet } from 'react-helmet';
import FAQSection from '../components/FAQSection';

const ContentWriter = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(null);
  const [formData, setFormData] = useState({
    type: 'blog',
    topic: '',
    keywords: '',
    tone: 'professional',
    length: 'medium',
    audience: 'general',
    includeFAQ: true,
    includeSchema: true
  });

  const contentTypes = [
    { value: 'blog', label: 'Blog Post', icon: DocumentTextIcon },
    { value: 'landing', label: 'Landing Page', icon: DocumentTextIcon },
    { value: 'product', label: 'Product Description', icon: DocumentTextIcon },
    { value: 'faq', label: 'FAQ Section', icon: DocumentTextIcon },
    { value: 'social', label: 'Social Media Posts', icon: DocumentTextIcon },
    { value: 'email', label: 'Email Copy', icon: DocumentTextIcon },
    { value: 'meta', label: 'Meta Tags', icon: DocumentTextIcon },
    { value: 'howto', label: 'How-To Guide', icon: DocumentTextIcon },
    { value: 'listicle', label: 'Listicle', icon: DocumentTextIcon },
    { value: 'comparison', label: 'Comparison Article', icon: DocumentTextIcon }
  ];

  const tones = [
    { value: 'professional', label: 'Professional' },
    { value: 'casual', label: 'Casual' },
    { value: 'friendly', label: 'Friendly' },
    { value: 'authoritative', label: 'Authoritative' },
    { value: 'conversational', label: 'Conversational' }
  ];

  const lengths = [
    { value: 'short', label: 'Short (300-500 words)' },
    { value: 'medium', label: 'Medium (800-1200 words)' },
    { value: 'long', label: 'Long (1500-2500 words)', pro: true },
    { value: 'comprehensive', label: 'Comprehensive (3000+ words)', pro: true }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleGenerate = async () => {
    if (!formData.topic) {
      toast.error('Please enter a topic');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post('/api/content/generate', {
        ...formData,
        keywords: formData.keywords.split(',').map(k => k.trim()).filter(Boolean)
      });

      setGeneratedContent(response.data.content);
      toast.success('Content generated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to generate content');
      if (error.response?.status === 403) {
        // Show upgrade prompt
        toast.error('Upgrade to Pro for longer content');
      }
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const downloadContent = (content, filename = 'content.md') => {
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Downloaded successfully!');
  };

  const isHtmlContent = (str) => {
    if (typeof str !== "string") return false;
    return /<[a-z][\s\S]*>/i.test(str);
  };

  const formatContent = (content) => {
    if (typeof content === 'string') {
      return content;
    }
    

    // If content has HTML in .content field, extract and return body/article HTML
    if (content.content && isHtmlContent(content.content)) {
      // Strip markdown code fences that OpenAI sometimes wraps around HTML
      let htmlStr = content.content;
      htmlStr = htmlStr.replace(/^```html\s*/i, "").replace(/```\s*$/, "");
      htmlStr = htmlStr.replace(/^```\s*/i, "").replace(/```\s*$/, "");
      const bodyMatch = htmlStr.match(/<body[^>]*>([\s\S]*)<\/body>/i);
      if (bodyMatch) return bodyMatch[1].trim();
      const articleMatch = htmlStr.match(/<article[^>]*>([\s\S]*)<\/article>/i);
      if (articleMatch) return articleMatch[1].trim();
      return htmlStr;
    }

    // Format different content types
    if (content.title) {
      let formatted = `# ${content.title}\n\n`;
      if (content.metaDescription) {
        formatted += `**Meta Description:** ${content.metaDescription}\n\n`;
      }
      if (content.content) {
        formatted += content.content;
      } else if (content.sections) {
        Object.entries(content.sections).forEach(([key, value]) => {
          if (value) {
            formatted += `\n## ${key.charAt(0).toUpperCase() + key.slice(1)}\n\n${value}\n`;
          }
        });
      }
      return formatted;
    }
    
    return JSON.stringify(content, null, 2);
  };


  const WRITER_FAQ = [
    { q: "What is an AI content writer and how does it work?", a: "An AI content writer uses artificial intelligence and natural language processing to generate human-quality written content. SERPfinity's AI writer combines large language model technology with real-time SEO data, competitive analysis, and AEO optimization signals to create content that is not only well-written but strategically optimized for search engines and AI answer platforms from the moment of creation." },
    { q: "Is AI-generated content good for SEO?", a: "Google has confirmed that AI-generated content is acceptable for ranking purposes as long as it provides genuine value to users. SERPfinity's AI writer creates content that meets Google's E-E-A-T guidelines by incorporating expert-level depth, original insights, proper citations, and comprehensive topic coverage. Our tool ensures AI content is optimized, unique, and designed to rank rather than generic AI output." },
    { q: "How does SERPfinity ensure content is AEO optimized?", a: "SERPfinity's content writer automatically structures content for AI search citation by including clear question-and-answer sections, implementing proper heading hierarchy, using entity-rich language, adding schema-ready content blocks, incorporating authoritative data points, and formatting information in ways that AI assistants can easily extract and cite as answers to user queries." },
    { q: "Can I customize the tone and style of AI-written content?", a: "Yes, SERPfinity's AI writer supports multiple content tones including professional, casual, authoritative, conversational, and technical. You can specify your brand voice guidelines, target audience, reading level, and content format preferences. The AI adapts its writing style while maintaining SEO optimization, ensuring your content sounds authentic to your brand while ranking effectively." },
    { q: "What types of content can the AI writer create?", a: "SERPfinity's AI writer generates blog posts, long-form articles, product descriptions, landing page copy, meta descriptions, social media content, FAQ sections, how-to guides, comparison articles, and listicles. Each content type is optimized with the appropriate SEO structure, keyword placement, and formatting for its intended purpose and platform." },
    { q: "How does AI content writing complement my content strategy?", a: "AI content writing accelerates your content production pipeline while maintaining quality and SEO optimization. It helps overcome writer's block, generates first drafts rapidly, ensures consistent keyword optimization, identifies content gaps to fill, scales content creation for topic clusters, and maintains SEO best practices across all content. Human editors can then refine and add unique expertise and brand personality." }
  ];

  return (
    <>
      <Helmet>
        <title>AI Content Writer | SEO & AEO Optimized Content Generator | SERPfinity</title>
        <meta name="description" content="Generate SEO and AEO optimized content with AI. Create blog posts, articles, and web copy that ranks in search engines and gets cited by AI assistants using SERPfinity content writer." />
        <meta name="keywords" content="AI content writer, SEO content generator, AEO optimized content, blog post writer, article generator, AI copywriting, content creation tool, SEO writing assistant, SERPfinity" />
        <link rel="canonical" href="https://app.serpfinity.ai/content-writer" />
        <meta property="og:title" content="AI Content Writer | SERPfinity" />
        <meta property="og:description" content="Generate SEO and AEO optimized content with AI. Create ranking-ready blog posts, articles, and web copy." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://app.serpfinity.ai/content-writer" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AI Content Writer | SERPfinity" />
        <meta name="twitter:description" content="AI-powered content writing for SEO professionals. Generate optimized articles, blog posts, and web copy." />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">{JSON.stringify({"@context":"https://schema.org","@type":"FAQPage","mainEntity":WRITER_FAQ.map(item=>({"@type":"Question","name":item.q,"acceptedAnswer":{"@type":"Answer","text":item.a}}))})}</script>
      </Helmet>
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <SparklesIcon className="h-8 w-8 text-purple-600 mr-3" />
                AI Content Writer
              </h1>
              <p className="text-gray-600 mt-2">
                Generate SEO-optimized content with AI
              </p>
            </div>
            {!user && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  Login to unlock Pro features like long-form content
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Content Generation Form */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">Content Settings</h2>
              
              {/* Content Type */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Content Type
                </label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  {contentTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Topic */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Topic or Title
                </label>
                <input
                  type="text"
                  name="topic"
                  value={formData.topic}
                  onChange={handleInputChange}
                  placeholder="e.g., Best SEO practices for 2025"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Keywords */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Keywords (comma-separated)
                </label>
                <input
                  type="text"
                  name="keywords"
                  value={formData.keywords}
                  onChange={handleInputChange}
                  placeholder="SEO, optimization, ranking"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Tone */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tone
                </label>
                <select
                  name="tone"
                  value={formData.tone}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  {tones.map(tone => (
                    <option key={tone.value} value={tone.value}>
                      {tone.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Length */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Content Length
                </label>
                <select
                  name="length"
                  value={formData.length}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  {lengths.map(length => (
                    <option 
                      key={length.value} 
                      value={length.value}
                      disabled={length.pro && (!user || user.plan === 'free')}
                    >
                      {length.label} {length.pro && '(Pro)'}
                    </option>
                  ))}
                </select>
              </div>

              {/* Audience */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Audience
                </label>
                <input
                  type="text"
                  name="audience"
                  value={formData.audience}
                  onChange={handleInputChange}
                  placeholder="e.g., Marketing professionals"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Options */}
              <div className="mb-6 space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="includeFAQ"
                    checked={formData.includeFAQ}
                    onChange={handleInputChange}
                    className="mr-2 rounded text-blue-600"
                  />
                  <span className="text-sm text-gray-700">Include FAQ Section</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="includeSchema"
                    checked={formData.includeSchema}
                    onChange={handleInputChange}
                    className="mr-2 rounded text-blue-600"
                  />
                  <span className="text-sm text-gray-700">Include Schema Markup</span>
                </label>
              </div>

              {/* Generate Button */}
              <button
                onClick={handleGenerate}
                disabled={loading}
                className="w-full px-4 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <SparklesIcon className="h-5 w-5 mr-2" />
                    Generate Content
                  </span>
                )}
              </button>
            </div>

            {/* Tips */}
            <div className="bg-blue-50 rounded-lg p-4 mt-4">
              <div className="flex items-start">
                <LightBulbIcon className="h-5 w-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-semibold text-blue-900">Pro Tips</h3>
                  <ul className="mt-2 text-sm text-blue-700 space-y-1">
                    <li>• Be specific with your topic for better results</li>
                    <li>• Include 3-5 relevant keywords</li>
                    <li>• Choose tone based on your audience</li>
                    <li>• FAQs improve AI Overview visibility</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Generated Content Display */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow min-h-[600px]">
              {generatedContent ? (
                <div className="p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold">Generated Content</h2>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => copyToClipboard(formatContent(generatedContent))}
                        className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-md flex items-center text-sm"
                      >
                        <ClipboardDocumentIcon className="h-4 w-4 mr-1" />
                        Copy
                      </button>
                      <button
                        onClick={() => downloadContent(formatContent(generatedContent), `${formData.topic.replace(/\s+/g, '-')}.md`)}
                        className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-md flex items-center text-sm"
                      >
                        <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                        Download
                      </button>
                    </div>
                  </div>

                  {/* Metadata */}
                  {generatedContent.metadata && (
                    <div className="grid grid-cols-4 gap-4 mb-6">
                      <div className="bg-gray-50 rounded p-3">
                        <p className="text-xs text-gray-500">Word Count</p>
                        <p className="text-lg font-semibold">{generatedContent.metadata.wordCount}</p>
                      </div>
                      <div className="bg-gray-50 rounded p-3">
                        <p className="text-xs text-gray-500">Reading Time</p>
                        <p className="text-lg font-semibold">{generatedContent.metadata.readingTime} min</p>
                      </div>
                      <div className="bg-gray-50 rounded p-3">
                        <p className="text-xs text-gray-500">SEO Score</p>
                        <p className="text-lg font-semibold">{generatedContent.metadata.seoScore}/100</p>
                      </div>
                      <div className="bg-gray-50 rounded p-3">
                        <p className="text-xs text-gray-500">AI Ready</p>
                        <p className="text-lg font-semibold">{generatedContent.metadata.aiReadiness}/100</p>
                      </div>
                    </div>
                  )}

                  {/* Content Display */}
              <div className="prose max-w-none">
                {(() => {
                  const formatted = formatContent(generatedContent);
                  if (isHtmlContent(formatted)) {
                    return (
                      <div
                        className="text-gray-800 content-preview [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mb-4 [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:mt-6 [&_h2]:mb-3 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-4 [&_h3]:mb-2 [&_p]:mb-4 [&_p]:leading-relaxed [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_li]:mb-2 [&_strong]:font-semibold"
                        dangerouslySetInnerHTML={{ __html: formatted }}
                      />
                    );
                  }
                  return (
                    <pre className="whitespace-pre-wrap font-sans text-gray-800">
                      {formatted}
                    </pre>
                  );
                })()}
              </div>

                  {/* Schema Display */}
                  {generatedContent.schema && (
                    <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                      <h3 className="text-sm font-semibold mb-2">Schema Markup</h3>
                      <pre className="text-xs bg-white p-3 rounded overflow-x-auto">
                        {JSON.stringify(generatedContent.schema, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-6 flex flex-col items-center justify-center h-[600px] text-gray-400">
                  <DocumentTextIcon className="h-16 w-16 mb-4" />
                  <p className="text-lg font-medium">No content generated yet</p>
                  <p className="text-sm mt-2">Configure settings and click Generate Content</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
      <FAQSection items={WRITER_FAQ} />
    </>
  );
};

export default ContentWriter;